import { MessageBarType } from '@fluentui/react';
import { action, computed, makeObservable, observable } from 'mobx';
import React from 'react';
import { cancelablePromise, DeferredPromise } from 'shared/utils/utils';
import MessagePane, { Button, Type } from '../components/base/MessagePane';

interface Task {
  text?: string;
  description?: string;
  cancelToken?: () => void;
  promise?: DeferredPromise;
  buttons?: Button[];
  withSpinner?: boolean;
}

interface ShowPaneOptions {
  message?: string;
  buttons?: Button[];
  type?: Type;
  timeout?: number;
}

interface DirtyDialogOptions {
  text: string;
  onSave(): void;
  onForget(): void;
  onDismiss(): void;
}

interface LicenseMessagePaneOptions {
  message: React.ReactNode;
  paneType: string;
  onUpgrade(): void;
  onClose(): void;
}

interface LicenseOverlayOptions {
  title: string;
  text: string;
  button: {
    text: string;
    onClick(): void;
  };
  onClose(): void;
}

export default class RequestStore {
  @observable private tasks: Task[] = [];
  @observable private interaction: boolean = true;
  @observable private messagePane: Parameters<typeof MessagePane>[0] | null = null;
  @observable private dirtyDialog: DirtyDialogOptions | null = null;
  @observable licenseMessagePane: LicenseMessagePaneOptions | null = null;
  @observable licenseOverlay: LicenseOverlayOptions | null = null;

  constructor() {
    makeObservable(this);
  }

  private timeoutCancelFunction: (() => void) | null = null;

  @computed get message() {
    return this.messagePane?.message ?? '';
  }

  @computed get activeTask() {
    // Find the last one, which has overlay with button or text
    const t = this.tasks.filter((x) => x.cancelToken || x.text);
    return t.length ? t[t.length - 1] : this.tasks.length ? this.tasks[this.tasks.length - 1] : null;
  }

  @action raise(message: string, clean = false): void {
    if (clean) {
      this.clean();
    }
    this.showPane({ message, type: MessageBarType.error });
  }

  @action warn(message: string, timeout: number = 0): void {
    this.showPane({ message, type: MessageBarType.warning, timeout });
  }

  @action success(message: string, timeout: number = 0): void {
    this.showPane({ message, type: MessageBarType.success, timeout });
  }

  @action showPane({ message = '', buttons = [], type = MessageBarType.error, timeout = 0 }: ShowPaneOptions): void {
    this.clean();

    if (timeout > 0) {
      let timeoutHandle: number | NodeJS.Timeout | null | undefined = null;

      this.timeoutCancelFunction = cancelablePromise(
        (resolve: any) => {
          timeoutHandle = setTimeout(() => {
            timeoutHandle && clearTimeout(timeoutHandle);
            timeoutHandle = null;
            this._cleanWithoutCancel();
            resolve(message);
          }, timeout);
        },
        () => timeoutHandle && clearTimeout(timeoutHandle),
      ).cancelFunction;
    }

    this.messagePane = { message, buttons, messageType: type as Type };
  }

  @action showDirtyDialog({ text, onDismiss, onForget, onSave }: DirtyDialogOptions) {
    const wrapCallback = (callback: () => void) => {
      return () => {
        callback();
        this.dirtyDialog = null;
      };
    };

    this.dirtyDialog = {
      text,
      onSave: wrapCallback(onSave),
      onForget: wrapCallback(onForget),
      onDismiss: wrapCallback(onDismiss),
    };
  }

  @action showLicensePane({
    message,
    paneType,
    onUpgrade,
  }: {
    message: React.ReactNode;
    paneType: string;
    onUpgrade(): void;
  }) {
    this.licenseMessagePane = {
      message,
      paneType,
      onUpgrade,
      onClose: action(() => {
        this.licenseMessagePane = null;
      }),
    };
  }

  @action showLicenseOverlay({
    title,
    text,
    button,
  }: {
    title: string;
    text: string;
    button: { text: string; onClick(): void };
  }) {
    this.licenseOverlay = {
      title,
      text,
      button,
      onClose: action(() => {
        this.licenseOverlay = null;
      }),
    };
  }

  @action clean() {
    this.timeoutCancelFunction && this.timeoutCancelFunction();
    this._cleanWithoutCancel();
  }

  @action startBlockedOperation(data?: { text?: string; cancelToken?: () => void }) {
    this.tasks.push(data ? { text: data.text || 'Executing...', cancelToken: data.cancelToken } : {});
  }

  @action setText(text: string, { description = ' ', withSpinner = false } = {}) {
    const index = this.tasks.length - 1;
    if (index !== -1) {
      const t = this.tasks[index];
      this.tasks[index] = { ...t, text, description, withSpinner };
    }
  }

  @action showDialog(text: string, buttons: Button[]): Promise<string> {
    const promise = new DeferredPromise();
    this.tasks.push({
      text,
      promise,
      buttons: buttons.map((x) => ({
        ...x,
        onClick: () => {
          this.stopBlockedOperation();
          promise.resolve(x.key);
        },
      })),
    });
    return promise.promise;
  }

  @action stopBlockedOperation() {
    if (this.tasks.length) {
      this.tasks = this.tasks.slice(0, -1);
    }
  }

  @action disableInteraction() {
    this.startBlockedOperation();
  }

  @action enableInteraction() {
    this.stopBlockedOperation();
  }

  @action _cleanWithoutCancel() {
    this.timeoutCancelFunction = null;
    this.messagePane = null;
  }
}
